// Deterministic filing-status inference. Used when the source data does not
// include filing status. Produces a STABLE per-employee assignment based on a
// hash of (clientId, employeeId) -- never Math.random() -- so the same
// employee always lands in the same bucket across re-runs / page reloads /
// PDF exports / recalculations. Marked with `filingStatusInferred: true`.

// Standard 50 / 35 / 15 distribution (single / mfj / hoh) — overridable.
export const DEFAULT_FILING_DISTRIBUTION = {
  single: 0.50,
  married: 0.35, // canonical engine value 'married'
  head: 0.15,    // canonical engine value 'head'
};

// Simple FNV-1a 32-bit hash → unsigned int. Used as a deterministic seed.
function fnv1aHash(input) {
  let hash = 0x811c9dc5; // 2166136261
  const str = String(input || '');
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0; // 16777619, force unsigned
  }
  return hash >>> 0;
}

// Map hash to [0, 1)
function hashToUnit(input) {
  return fnv1aHash(input) / 0xffffffff;
}

export function pickInferredFiling(seedInput, distribution = DEFAULT_FILING_DISTRIBUTION) {
  const u = hashToUnit(seedInput);
  const single = distribution.single ?? 0.50;
  const married = distribution.married ?? 0.35;
  // const head = 1 - single - married  (remainder)
  if (u < single) return 'single';
  if (u < single + married) return 'married';
  return 'head';
}

// Apply inferred filing to an array of employees. Mutates copies; returns a
// new array. Employees whose filing is already set are left alone.
//
// `clientId` is optional but recommended -- it ensures different clients with
// overlapping employee IDs land in different (still deterministic) buckets.
export function applyInferredFiling(employees, options = {}) {
  const {
    clientId = '',
    distribution = DEFAULT_FILING_DISTRIBUTION,
    strategy = 'distribution', // 'distribution' | 'all_single' | 'aggregate' | 'column'
    aggregateOverride = null,  // { single, married, head } proportions when strategy='aggregate'
    columnValues = null,       // pre-mapped per-employee strings when strategy='column'
  } = options;

  const dist = strategy === 'aggregate' && aggregateOverride ? aggregateOverride : distribution;

  return employees.map((emp, idx) => {
    if (emp.filing && emp.filing !== '' && !emp.filingStatusInferred) {
      return emp; // already has real data
    }
    if (strategy === 'all_single') {
      return { ...emp, filing: 'single', filingStatusInferred: true };
    }
    if (strategy === 'column' && columnValues) {
      const raw = columnValues[idx];
      if (raw) {
        return { ...emp, filing: raw, filingStatusInferred: false };
      }
    }
    const seed = `${clientId}|${emp.employeeId || emp.id || emp.email || `idx-${idx}`}`;
    return {
      ...emp,
      filing: pickInferredFiling(seed, dist),
      filingStatusInferred: true,
    };
  });
}

// Pre-flight summary of what the strategy would assign, without mutating the
// employees -- so the UI can preview "23 single / 16 mfj / 6 hoh" before the
// user accepts.
export function previewInferredFilingDistribution(employees, options = {}) {
  const applied = applyInferredFiling(employees, options);
  const counts = { single: 0, married: 0, head: 0, other: 0 };
  applied.forEach((e) => {
    if (counts[e.filing] !== undefined) counts[e.filing] += 1;
    else counts.other += 1;
  });
  return counts;
}
